import React, { useEffect, useRef, useState } from 'react';
import axios from '../api/client';
import { API } from '../api/config';
import { MessageCircle, X, Send } from 'lucide-react';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

// Matches either a markdown link "[text](url)" or a bare http(s) URL, so
// whichever style the LLM happens to produce still renders as a clickable
// link — chat messages are plain text otherwise (no markdown/HTML support),
// so this is the one bit of formatting we parse out ourselves.
const LINK_PATTERN = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|(https?:\/\/[^\s)]+)/g;

function renderMessageContent(content: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;

  while ((match = LINK_PATTERN.exec(content)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(content.slice(lastIndex, match.index));
    }
    const [full, markdownText, markdownUrl, bareUrl] = match;
    const url = markdownUrl || bareUrl;
    const label = markdownText || bareUrl;
    nodes.push(
      <a key={key++} href={url} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>
        {label}
      </a>
    );
    lastIndex = match.index + full.length;
  }
  if (lastIndex < content.length) {
    nodes.push(content.slice(lastIndex));
  }
  return nodes;
}

const SESSION_STORAGE_KEY = 'incord_chat_session_id';
const HISTORY_STORAGE_KEY = 'incord_chat_history';
const MAX_STORED_MESSAGES = 50;

function getOrCreateSessionId(): string {
  try {
    const existing = localStorage.getItem(SESSION_STORAGE_KEY);
    if (existing) return existing;
    const fresh = crypto.randomUUID();
    localStorage.setItem(SESSION_STORAGE_KEY, fresh);
    return fresh;
  } catch {
    // Private browsing / storage blocked — fall back to an in-memory id
    // that just won't survive a refresh.
    return crypto.randomUUID();
  }
}

// The backend's own conversation history is in-memory per worker process
// (lost on restart/redeploy), so persisting the visible thread here is
// what actually survives a page reload — including the one that happens
// when a customer goes to log in.
function loadStoredMessages(): ChatMessage[] {
  try {
    const raw = localStorage.getItem(HISTORY_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (m): m is ChatMessage =>
        m && (m.role === 'user' || m.role === 'assistant') && typeof m.content === 'string'
    );
  } catch {
    return [];
  }
}

function saveStoredMessages(messages: ChatMessage[]): void {
  try {
    localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(messages.slice(-MAX_STORED_MESSAGES)));
  } catch {
    // Private browsing / storage full / blocked — history just won't persist.
  }
}

const ChatWidget: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>(loadStoredMessages);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const sessionIdRef = useRef<string>('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  if (!sessionIdRef.current) {
    sessionIdRef.current = getOrCreateSessionId();
  }

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open]);

  useEffect(() => {
    saveStoredMessages(messages);
  }, [messages]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || sending) return;

    setMessages((current) => [...current, { role: 'user', content: text }]);
    setInput('');
    setSending(true);
    setError('');

    try {
      const response = await axios.post(API.sendChatMessage(), {
        session_id: sessionIdRef.current,
        message: text,
      });
      setMessages((current) => [...current, { role: 'assistant', content: response.data.reply }]);
    } catch (err: any) {
      setError(
        err.response?.status === 503
          ? 'Our sourcing concierge is not available right now — please try again shortly.'
          : "Something went wrong sending that — please try again."
      );
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div style={{ position: 'fixed', bottom: '24px', right: '24px', zIndex: 2000 }}>
      {open && (
        <div
          style={{
            width: 'min(360px, calc(100vw - 48px))',
            height: 'min(520px, calc(100vh - 140px))',
            background: 'var(--white, #fff)',
            borderRadius: '16px',
            boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            marginBottom: '16px',
            border: '1px solid var(--border-light, rgba(0,0,0,0.1))',
          }}
        >
          <div
            style={{
              background: 'var(--text-charcoal, #222524)',
              color: '#fff',
              padding: '16px 20px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <div>
              <div style={{ fontFamily: 'var(--serif-font, serif)', fontSize: '16px', fontWeight: 600 }}>
                AI Sourcing Concierge
              </div>
              <div style={{ fontSize: '11px', opacity: 0.7, marginTop: '2px' }}>Incord · usually replies in seconds</div>
            </div>
            <button
              onClick={() => setOpen(false)}
              aria-label="Close chat"
              style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', padding: '4px' }}
            >
              <X size={20} />
            </button>
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: '10px', background: 'var(--bg-oatmeal, #F7F5F2)' }}>
            {messages.length === 0 && (
              <div style={{ fontSize: '13px', color: 'var(--text-charcoal, #222524)', opacity: 0.7, lineHeight: 1.5 }}>
                Tell me what you're looking to source — a designer piece, an accessory, a
                specific brand — and I'll help find real options or pass your request to
                our sourcing team.
              </div>
            )}
            {messages.map((m, idx) => (
              <div
                key={idx}
                style={{
                  alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                  maxWidth: '85%',
                  background: m.role === 'user' ? 'var(--accent-moss, #4A5D23)' : '#fff',
                  color: m.role === 'user' ? '#fff' : 'var(--text-charcoal, #222524)',
                  border: m.role === 'user' ? 'none' : '1px solid var(--border-light, rgba(0,0,0,0.1))',
                  borderRadius: '12px',
                  padding: '10px 14px',
                  fontSize: '13px',
                  lineHeight: 1.5,
                  whiteSpace: 'pre-wrap',
                }}
              >
                {renderMessageContent(m.content)}
              </div>
            ))}
            {sending && (
              <div style={{ alignSelf: 'flex-start', fontSize: '12px', color: 'var(--text-charcoal, #222524)', opacity: 0.6 }}>
                Typing...
              </div>
            )}
            {error && (
              <div style={{ fontSize: '12px', color: '#DC2626' }}>{error}</div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div style={{ padding: '12px', borderTop: '1px solid var(--border-light, rgba(0,0,0,0.1))', display: 'flex', gap: '8px', alignItems: 'flex-end', background: '#fff' }}>
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask about a product or item you're looking for..."
              rows={1}
              style={{
                flex: 1,
                resize: 'none',
                border: '1px solid var(--border-light, rgba(0,0,0,0.15))',
                borderRadius: '8px',
                padding: '10px 12px',
                fontSize: '13px',
                fontFamily: 'var(--sans-font, sans-serif)',
                maxHeight: '80px',
              }}
            />
            <button
              onClick={sendMessage}
              disabled={sending || !input.trim()}
              aria-label="Send message"
              style={{
                background: 'var(--accent-moss, #4A5D23)',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                width: '38px',
                height: '38px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: sending || !input.trim() ? 'not-allowed' : 'pointer',
                opacity: sending || !input.trim() ? 0.5 : 1,
                flexShrink: 0,
              }}
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      )}

      <button
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? 'Close chat' : 'Open AI Sourcing Concierge chat'}
        style={{
          width: '56px',
          height: '56px',
          borderRadius: '50%',
          background: 'var(--accent-moss, #4A5D23)',
          color: '#fff',
          border: 'none',
          boxShadow: '0 6px 20px rgba(0,0,0,0.25)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginLeft: 'auto',
        }}
      >
        {open ? <X size={24} /> : <MessageCircle size={24} />}
      </button>
    </div>
  );
};

export default ChatWidget;
